import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Footer } from '../components/Footer';
import { ResourceDetailModal } from '../components/marketplace/ResourceDetailModal';
import { useStaggeredReveal } from '../components/marketplace/useStaggeredReveal';
import { formatResourceCount, IconBadge, ResourceEmptyState, ResourceFilterRow, ResourceMarketHero, ResourcePagination, ResourceRating, ResourceSearchBox, } from '../components/marketplace/MarketplaceShared';
import { getPluginFilters, getPlugins } from '../services/marketplaceApi';
import './marketplace.scss';
const PAGE_SIZE = 10;
const COUNT_PAGE_SIZE = 10000;
const emptyPluginFilters = {
    types: [],
    sources: [],
    tenants: [],
};
const categoryToneNames = ['blue', 'cyan', 'purple', 'green', 'amber', 'slate'];
function toneFromKey(prefix, key) {
    const seed = key || prefix;
    const index = Math.abs(seed.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)) % categoryToneNames.length;
    return `${prefix}--cat-${categoryToneNames[index]}`;
}
function formatVersion(value) {
    if (!value)
        return '';
    return value.toLowerCase().startsWith('v') ? value : `v${value}`;
}
function getPluginTenantTag(plugin) {
    if (plugin.tenantKey === '0')
        return '官方插件';
    return '租户插件';
}
function getTenantToneClass(label) {
    return label === '官方插件' ? 'resource-plugin-tag--official' : 'resource-plugin-tag--tenant';
}
function buildTypeCounts(items) {
    return items.reduce((result, plugin) => {
        plugin.typeKeys.forEach((key) => {
            result[key] = (result[key] || 0) + 1;
        });
        return result;
    }, {});
}
export default function PluginMarketPage() {
    const [keyword, setKeyword] = useState('');
    const [pluginType, setPluginType] = useState('');
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState(emptyPluginFilters);
    const [items, setItems] = useState([]);
    const [countItems, setCountItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedPlugin, setSelectedPlugin] = useState(null);
    const pageRef = useStaggeredReveal([loading, items.length, total, page, pluginType, keyword]);
    useEffect(() => {
        let active = true;
        getPluginFilters().then((res) => {
            if (active)
                setFilters(res);
        });
        return () => {
            active = false;
        };
    }, []);
    useEffect(() => {
        let active = true;
        setLoading(true);
        Promise.all([
            getPlugins({ keyword, pluginType, page, pageSize: PAGE_SIZE }),
            getPlugins({ keyword, page: 1, pageSize: COUNT_PAGE_SIZE }),
        ]).then(([res, countRes]) => {
            if (!active)
                return;
            setItems(res.items);
            setCountItems(countRes.items);
            setTotal(res.total);
            setPage(res.page);
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [keyword, pluginType, page]);
    const resetPage = (update) => {
        setPage(1);
        update();
    };
    const openPluginDetail = (plugin) => {
        setSelectedPlugin(plugin);
    };
    const handlePluginCardKeyDown = (event, plugin) => {
        if (event.key !== 'Enter' && event.key !== ' ')
            return;
        event.preventDefault();
        openPluginDetail(plugin);
    };
    const typeCounts = buildTypeCounts(countItems);
    return (_jsxs(_Fragment, { children: [_jsxs("main", { className: "resource-market-page", ref: pageRef, children: [_jsxs("section", { className: "resource-market-shell", children: [_jsx(ResourceMarketHero, { type: "plugins", eyebrow: "YONCODE PLUGIN MARKET", title: "\u63D2\u4EF6\u5E02\u573A", description: "\u6C47\u805A YonCode \u5B98\u65B9\u53CA\u901A\u7528\u5F00\u53D1\u63D2\u4EF6\uFF0C\u63D0\u4F9B\u4E30\u5BCC\u7684\u5F00\u53D1\u80FD\u529B\u6269\u5C55\uFF0C\u52A9\u529B\u4F01\u4E1A\u9AD8\u6548\u6784\u5EFA\u3001\u7075\u6D3B\u5B9A\u5236\u5404\u7C7B\u4E1A\u52A1\u5E94\u7528\u3002" }), _jsxs("section", { className: "resource-market-board", "data-resource-market": "plugins", "aria-labelledby": "plugins-market-title", children: [_jsxs("div", { className: "resource-discovery-panel reveal", children: [_jsx("div", { className: "resource-market-searchbar", children: _jsx(ResourceSearchBox, { value: keyword, placeholder: "\u641C\u7D22\u63D2\u4EF6\u540D\u79F0\u3001\u8D44\u6E90\u7C7B\u578B\u6216\u6765\u6E90...", onChange: (value) => resetPage(() => setKeyword(value)) }) }), _jsx("div", { className: "resource-market-layout", children: _jsx("aside", { className: "resource-toolbar resource-toolbar--plugin-filters", "aria-label": "\u63D2\u4EF6\u7B5B\u9009", children: _jsx("div", { className: "resource-filter-stack", children: _jsx(ResourceFilterRow, { label: "\u63D2\u4EF6\u5206\u7C7B", options: filters.types, value: pluginType, totalCount: countItems.length, counts: typeCounts, onChange: (value) => resetPage(() => setPluginType(value)) }) }) }) })] }), _jsxs("div", { className: "resource-results-panel", children: [_jsx("div", { className: "resource-result-line", children: _jsx("p", { className: "resource-status", id: "plugins-market-title", children: loading ? '加载插件中...' : `共 ${total} 个插件` }) }), loading ? (_jsx("div", { className: "market-loading resource-loading reveal", children: "\u52A0\u8F7D\u63D2\u4EF6\u4E2D..." })) : items.length ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "resource-grid reveal", children: items.map((plugin) => {
                                                            const primaryType = plugin.typeLabels[0] || '插件';
                                                            const primaryTypeKey = plugin.typeKeys[0] || primaryType;
                                                            const tenantTag = getPluginTenantTag(plugin);
                                                            const packageLine = plugin.packageName && plugin.packageName !== plugin.name
                                                                ? plugin.packageName
                                                                : formatVersion(plugin.version) || plugin.packageName;
                                                            return (_jsxs("article", { className: "resource-card resource-card--plugin", role: "button", tabIndex: 0, "aria-label": `查看 ${plugin.name} 详情`, onClick: () => openPluginDetail(plugin), onKeyDown: (event) => handlePluginCardKeyDown(event, plugin), children: [_jsx(IconBadge, { icon: "plugin", color: plugin.color, label: plugin.name, imageSrc: plugin.iconUrl, variant: "resource" }), _jsxs("div", { className: "resource-plugin-main", children: [_jsxs("div", { className: "resource-plugin-title", children: [_jsx("h3", { children: plugin.name }), _jsxs("div", { className: "resource-card-subline", children: [_jsx("strong", { className: "resource-plugin-package", children: packageLine || '-' }), _jsx("span", { className: `resource-plugin-tag ${toneFromKey('resource-plugin-tag', primaryTypeKey)}`, children: primaryType }), _jsx("span", { className: `resource-plugin-tag ${getTenantToneClass(tenantTag)}`, children: tenantTag })] })] }), plugin.description && _jsx("p", { className: "resource-plugin-desc", children: plugin.description }), _jsxs("div", { className: "resource-plugin-meta", children: [_jsxs("span", { className: "resource-plugin-update", children: ["\u66F4\u65B0\u65F6\u95F4\uFF1A", plugin.updatedAt || '-'] }), _jsx("span", { className: "resource-plugin-heat", children: formatResourceCount(plugin.views) }), _jsx(ResourceRating, { value: plugin.rating })] })] })] }, plugin.id));
                                                        }) }), _jsx(ResourcePagination, { page: page, pageSize: PAGE_SIZE, total: total, onChange: setPage })] })) : (_jsx(ResourceEmptyState, {}))] })] })] }), _jsx(ResourceDetailModal, { kind: "plugins", item: selectedPlugin, visible: Boolean(selectedPlugin), onClose: () => setSelectedPlugin(null) })] }), _jsx(Footer, {})] }));
}
